import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Tabs, Tab, Card, CardContent,
  Grid, Avatar, Chip, Rating, TextField, InputAdornment,
  CircularProgress, Alert, List, ListItem, ListItemAvatar,
  ListItemText, ListItemSecondaryAction, IconButton, Divider,
  Stack, Badge
} from '@mui/material';
import {
  Search, Store, History, PersonAdd, Close, CheckCircle,
  LocationOn, Star, Verified, Email, Send, WorkHistory
} from '@mui/icons-material';
import { ContractorService } from '../../services/contractorService';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand, bg } from '../../theme/colors';

const FindContractorsDialog = ({ open, onClose, project, onContractorSelected, initialTab = 0, milestone = null }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Marketplace contractors
  const [marketplaceContractors, setMarketplaceContractors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContractors, setFilteredContractors] = useState([]);
  
  // Previous contractors
  const [previousContractors, setPreviousContractors] = useState([]);
  
  // Invite external contractor
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      loadData();
    }
  }, [open, initialTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Filter marketplace contractors based on search
    if (searchTerm) {
      const filtered = marketplaceContractors.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContractors(filtered);
    } else {
      setFilteredContractors(marketplaceContractors);
    }
  }, [searchTerm, marketplaceContractors]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 0) {
        // Load marketplace contractors
        await loadMarketplaceContractors();
      } else if (activeTab === 1) {
        // Load previous contractors
        await loadPreviousContractors();
      }
    } catch (err) {
      console.error('Error loading contractors:', err);
      setError(err.message || 'Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const loadMarketplaceContractors = async () => {
    try {
      const contractors = await ContractorService.getMarketplaceContractors({
        location: project?.location,
        specialization: project?.category
      });
      setMarketplaceContractors(contractors || []);
    } catch (err) {
      console.error('Error loading marketplace contractors:', err);
      setMarketplaceContractors([]);
    }
  };

  const loadPreviousContractors = async () => {
    try {
      const contractors = await ContractorService.getPreviousContractors(user.id);
      setPreviousContractors(contractors || []);
    } catch (err) {
      console.error('Error loading previous contractors:', err);
      setPreviousContractors([]);
    }
  };

  const handleInviteContractor = async () => {
    if (!inviteEmail || !inviteName) {
      setError('Please provide contractor name and email');
      return;
    }

    setInviting(true);
    setError('');

    try {
      // Send invitation email
      await api.post('/contractors/invite', {
        email: inviteEmail,
        name: inviteName,
        phone: invitePhone,
        message: inviteMessage,
        projectId: project?.id,
        projectTitle: project?.title,
        milestoneId: milestone?.id || null,
        milestoneName: milestone?.name || null,
        invitedBy: user.id
      });

      // Show success message
      alert(`Invitation sent to ${inviteName} at ${inviteEmail}`);
      
      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setInviteMessage('');
      
      onClose();
    } catch (err) {
      console.error('Error inviting contractor:', err);
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleSelectContractor = (contractor) => {
    if (onContractorSelected) {
      onContractorSelected(contractor);
    }
    onClose();
  };

  const renderMarketplaceTab = () => (
    <Box>
      <TextField
        fullWidth
        placeholder="Search contractors by name, specialization, or location..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
        sx={{ mb: 3 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredContractors.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Card
            sx={{
              maxWidth: 500,
              width: '100%',
              background: brand.gradient,
              color: 'white',
              borderRadius: 3,
              boxShadow: `0 8px 32px rgba(13, 148, 136, 0.4)`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 12px 40px rgba(13, 148, 136, 0.5)`
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 5, position: 'relative' }}>
              {/* Background Icon */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  opacity: 0.2
                }}
              >
                <Store sx={{ fontSize: 80 }} />
              </Box>

              {/* Icon */}
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  margin: '0 auto 20px',
                  fontSize: 40
                }}
              >
                <Store sx={{ fontSize: 50 }} />
              </Avatar>

              {/* Title */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Browse Contractor Marketplace
              </Typography>

              {/* Description */}
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.95, lineHeight: 1.6 }}>
                Discover verified contractors with proven track records. 
                Filter by specialization, location, and ratings to find the perfect match for your project.
              </Typography>

              {/* Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    500+
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Contractors
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    4.8★
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg Rating
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    10k+
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Projects Done
                  </Typography>
                </Box>
              </Box>

              {/* CTA Button */}
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  onClose();
                  // Pass project context to marketplace via URL params
                  const params = new URLSearchParams();
                  if (project?.id) params.append('projectId', project.id);
                  if (project?.title || project?.name) params.append('projectTitle', project.title || project.name);
                  if (milestone?.id) params.append('milestoneId', milestone.id);
                  if (milestone?.name) params.append('milestoneName', milestone.name);
                  window.location.href = `/contractors?${params.toString()}`;
                }}
                sx={{
                  bgcolor: 'white',
                  color: brand.primary,
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: bg.subtle,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Go to Marketplace →
              </Button>

              {/* Helper Text */}
              <Typography variant="caption" sx={{ display: 'block', mt: 3, opacity: 0.8 }}>
                Or invite a contractor using the "Invite Contractor" tab above
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredContractors.map((contractor) => (
            <Grid item xs={12} key={contractor.id}>
              <Card variant="outlined" sx={{ '&:hover': { boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                      src={contractor.profilePhoto}
                      sx={{ width: 64, height: 64 }}
                    >
                      {contractor.name?.[0]}
                    </Avatar>
                    
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6">
                          {contractor.name || `${contractor.firstName} ${contractor.lastName}`}
                        </Typography>
                        {contractor.verified && (
                          <Verified color="primary" fontSize="small" />
                        )}
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {contractor.specialization || 'General Contractor'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating value={contractor.rating || 4.5} readOnly size="small" precision={0.5} />
                          <Typography variant="body2" color="text.secondary">
                            ({contractor.reviewCount || 0})
                          </Typography>
                        </Box>
                        
                        {contractor.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {contractor.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {contractor.completedProjects > 0 && (
                          <Chip 
                            size="small" 
                            label={`${contractor.completedProjects} projects`}
                            icon={<WorkHistory />}
                          />
                        )}
                        {contractor.yearsExperience && (
                          <Chip 
                            size="small" 
                            label={`${contractor.yearsExperience} years exp.`}
                          />
                        )}
                      </Box>
                      
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (project?.id) params.append('projectId', project.id);
                          if (project?.title || project?.name) params.append('projectTitle', project.title || project.name);
                          params.append('contractorId', contractor.id);
                          if (milestone?.id) params.append('milestoneId', milestone.id);
                          if (milestone?.name) params.append('milestoneName', milestone.name);
                          window.location.href = `/contractors?${params.toString()}`;
                        }}
                        sx={{
                          background: brand.gradient,
                          '&:hover': {
                            background: brand.gradientHover
                          }
                        }}
                      >
                        Request Quote
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderPreviousTab = () => (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : previousContractors.length === 0 ? (
        <Alert severity="info">
          You haven't worked with any contractors yet. Browse the marketplace or invite a contractor.
        </Alert>
      ) : (
        <List>
          {previousContractors.map((contractor, index) => (
            <React.Fragment key={contractor.id}>
              {index > 0 && <Divider />}
              <ListItem alignItems="flex-start">
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      contractor.verified ? (
                        <CheckCircle color="primary" sx={{ fontSize: 16 }} />
                      ) : null
                    }
                  >
                    <Avatar src={contractor.profilePhoto}>
                      {contractor.name?.[0]}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {contractor.name || `${contractor.firstName} ${contractor.lastName}`}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {contractor.specialization}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Rating value={contractor.rating || 4.5} readOnly size="small" />
                        <Typography variant="caption" color="text.secondary">
                          • {contractor.projectsCompleted || 0} projects together
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleSelectContractor(contractor)}
                    >
                      Select
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const params = new URLSearchParams();
                        if (project?.id) params.append('projectId', project.id);
                        if (project?.title || project?.name) params.append('projectTitle', project.title || project.name);
                        params.append('contractorId', contractor.id);
                        if (milestone?.id) params.append('milestoneId', milestone.id);
                        if (milestone?.name) params.append('milestoneName', milestone.name);
                        window.location.href = `/contractors?${params.toString()}`;
                      }}
                      sx={{
                        background: brand.gradient,
                        '&:hover': {
                          background: brand.gradientHover
                        }
                      }}
                    >
                      Request Quote
                    </Button>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  const renderInviteTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Know a contractor who isn't on the platform yet? Invite them to join and work on your project.
        </Typography>
      </Alert>

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Contractor Name"
          value={inviteName}
          onChange={(e) => setInviteName(e.target.value)}
          placeholder="Enter contractor's full name"
          required
        />

        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="contractor@example.com"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            )
          }}
          required
        />

        <TextField
          fullWidth
          label="Phone Number (Optional)"
          value={invitePhone}
          onChange={(e) => setInvitePhone(e.target.value)}
          placeholder="+263 77 123 4567"
        />

        <TextField
          fullWidth
          label="Personal Message (Optional)"
          multiline
          rows={4}
          value={inviteMessage}
          onChange={(e) => setInviteMessage(e.target.value)}
          placeholder="Add a personal message to your invitation..."
          helperText="This message will be included in the invitation email"
        />

        <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            What happens next?
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              1. We'll send an invitation email with a personalized signup link
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2. They'll create an account on Eqaya with your project details pre-loaded
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3. A quote request is automatically created — they'll review your project scope
            </Typography>
            <Typography variant="body2" color="text.secondary">
              4. They respond with their quote and proposed timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              5. You accept the quote and the contractor is hired — ready to start!
            </Typography>
          </Stack>
        </Card>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleInviteContractor}
          disabled={inviting || !inviteEmail || !inviteName}
          startIcon={inviting ? <CircularProgress size={20} /> : <Send />}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          {inviting ? 'Sending Invitation...' : 'Send Invitation'}
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        Find Contractors
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab 
              icon={<Store />} 
              label="Marketplace" 
              iconPosition="start"
            />
            <Tab 
              icon={<History />} 
              label="Previous Contractors" 
              iconPosition="start"
            />
            <Tab 
              icon={<PersonAdd />} 
              label="Invite Contractor" 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Request a quote so the contractor can review your project scope and propose their price. Once you accept their quote, they're hired.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && renderMarketplaceTab()}
        {activeTab === 1 && renderPreviousTab()}
        {activeTab === 2 && renderInviteTab()}
      </DialogContent>
      
    </Dialog>
  );
};

export default FindContractorsDialog;
